import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, Trash2, Calendar as CalIcon, Pencil } from "lucide-react";
import { toast } from "sonner";

const fmtDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const m = ["jan", "feb", "mar", "apr", "maj", "jun", "jul", "aug", "sep", "okt", "nov", "dec"];
  return `${d.getDate()} ${m[d.getMonth()]}`;
};

const PRIORITY_COLOR = {
  high: "bg-[#E07A5F]",
  medium: "bg-[#F6CA83]",
  low: "bg-[color:var(--sage)]",
};

const emptyForm = { title: "", note: "", priority: "medium", deadline: "", list_type: "shared" };

export default function TodosPage() {
  const [list, setList] = useState("shared");
  const [todos, setTodos] = useState([]);
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [includeDone, setIncludeDone] = useState(false);

  const load = async () => {
    try {
      const [t, u] = await Promise.all([
        api.get(`/todos?list_type=${list}&include_done=${includeDone}`),
        api.get("/users"),
      ]);
      setTodos(t.data);
      setUsers(u.data);
    } catch (e) {
      // ignore
    }
  };

  useEffect(() => {
    load(); // eslint-disable-next-line
  }, [list, includeDone]);

  const openCreate = () => {
    setEditing(null);
    setForm({ ...emptyForm, list_type: list });
    setOpen(true);
  };

  const openEdit = (t) => {
    setEditing(t);
    setForm({
      title: t.title,
      note: t.note || "",
      priority: t.priority,
      deadline: t.deadline ? t.deadline.slice(0, 10) : "",
      list_type: t.list_type,
      assignee_id: t.assignee_id,
    });
    setOpen(true);
  };

  const save = async () => {
    if (!form.title.trim()) {
      toast.error("Skriv en titel");
      return;
    }
    const payload = {
      title: form.title.trim(),
      note: form.note,
      priority: form.priority,
      deadline: form.deadline ? new Date(form.deadline).toISOString() : null,
      assignee_id: form.assignee_id || null,
      list_type: form.list_type,
    };
    try {
      if (editing) await api.put(`/todos/${editing.id}`, payload);
      else await api.post("/todos", payload);
      toast.success(editing ? "Uppdaterad" : "Tillagd");
      setOpen(false);
      load();
    } catch (e) {
      toast.error("Kunde inte spara");
    }
  };

  const toggle = async (id) => {
    try {
      await api.post(`/todos/${id}/toggle`);
      load();
    } catch (e) {
      // ignore
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/todos/${id}`);
      toast.success("Borttagen");
      load();
    } catch (e) {
      toast.error("Kunde inte ta bort");
    }
  };

  return (
    <div className="px-5 pt-3" data-testid="todos-page">
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="label-eyebrow">Listor</p>
          <h1 className="font-display text-2xl font-medium mt-0.5">Att-göra</h1>
        </div>
        <Button
          onClick={openCreate}
          size="sm"
          className="rounded-full bg-[color:var(--moss)] hover:bg-[#1f421f] h-9"
          data-testid="add-todo-button"
        >
          <Plus size={16} className="mr-1" /> Ny
        </Button>
      </div>

      <Tabs value={list} onValueChange={setList} className="mb-3">
        <TabsList className="grid grid-cols-2 w-full bg-[color:var(--bg-elevated)]">
          <TabsTrigger value="shared" data-testid="list-shared-tab">Gemensam</TabsTrigger>
          <TabsTrigger value="personal" data-testid="list-personal-tab">Personlig</TabsTrigger>
        </TabsList>
      </Tabs>

      <label className="flex items-center gap-2 text-xs text-[color:var(--text-secondary)] mb-3">
        <Checkbox
          checked={includeDone}
          onCheckedChange={setIncludeDone}
          data-testid="include-done-checkbox"
        />
        Visa klara
      </label>

      {todos.length === 0 ? (
        <div className="card-soft p-8 text-center" data-testid="todos-empty">
          <p className="text-sm text-[color:var(--text-secondary)]">
            Inga uppgifter än. Lägg till din första.
          </p>
        </div>
      ) : (
        <ul className="space-y-2 stagger">
          {todos.map((t) => {
            const assignee = users.find((u) => u.id === t.assignee_id);
            return (
              <li
                key={t.id}
                className={`card-soft p-3.5 flex items-start gap-3 fade-up ${
                  t.completed ? "opacity-60" : ""
                }`}
                data-testid={`todo-${t.id}`}
              >
                <button
                  onClick={() => toggle(t.id)}
                  className={`mt-0.5 w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 ${
                    t.completed
                      ? "bg-[color:var(--moss)] border-[color:var(--moss)]"
                      : "border-[color:var(--border-strong)]"
                  }`}
                  data-testid={`todo-toggle-${t.id}`}
                >
                  {t.completed && (
                    <svg viewBox="0 0 16 16" className="w-3 h-3 text-white" fill="none">
                      <path d="M3 8l3 3 7-7" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
                    </svg>
                  )}
                </button>
                <div className="flex-1 min-w-0">
                  <p
                    className={`text-sm font-medium ${
                      t.completed ? "line-through text-[color:var(--text-tertiary)]" : ""
                    }`}
                  >
                    {t.title}
                  </p>
                  {t.note && (
                    <p className="text-xs text-[color:var(--text-secondary)] mt-1">{t.note}</p>
                  )}
                  <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                    <span
                      className={`w-2 h-2 rounded-full ${PRIORITY_COLOR[t.priority]}`}
                    />
                    <span className="text-[10px] uppercase tracking-wider text-[color:var(--text-tertiary)]">
                      {t.priority === "high" ? "Hög" : t.priority === "low" ? "Låg" : "Medel"}
                    </span>
                    {t.deadline && (
                      <span className="text-[10px] flex items-center gap-1 text-[color:var(--text-secondary)]">
                        <CalIcon size={10} /> {fmtDate(t.deadline)}
                      </span>
                    )}
                    {assignee && (
                      <span
                        className="text-[10px] px-1.5 py-0.5 rounded-full text-white font-medium"
                        style={{ backgroundColor: assignee.avatar_color }}
                      >
                        {assignee.name.split(" ")[0]}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex flex-col gap-1">
                  <button
                    onClick={() => openEdit(t)}
                    className="text-[color:var(--text-tertiary)] hover:text-[color:var(--text-primary)] p-1"
                    data-testid={`todo-edit-${t.id}`}
                  >
                    <Pencil size={14} />
                  </button>
                  <button
                    onClick={() => remove(t.id)}
                    className="text-[color:var(--text-tertiary)] hover:text-[#DC2626] p-1"
                    data-testid={`todo-delete-${t.id}`}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md rounded-2xl" data-testid="todo-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">{editing ? "Redigera" : "Ny uppgift"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <label className="label-eyebrow block mb-1.5">Titel</label>
              <Input
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="t.ex. Köpa mjölk"
                className="rounded-xl"
                data-testid="todo-title-input"
              />
            </div>
            <div>
              <label className="label-eyebrow block mb-1.5">Anteckning</label>
              <Textarea
                value={form.note}
                onChange={(e) => setForm({ ...form, note: e.target.value })}
                placeholder="Valfri info..."
                className="rounded-xl resize-none"
                rows={2}
                data-testid="todo-note-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label-eyebrow block mb-1.5">Prioritet</label>
                <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                  <SelectTrigger className="rounded-xl" data-testid="todo-priority-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Låg</SelectItem>
                    <SelectItem value="medium">Medel</SelectItem>
                    <SelectItem value="high">Hög</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="label-eyebrow block mb-1.5">Deadline</label>
                <Input
                  type="date"
                  value={form.deadline}
                  onChange={(e) => setForm({ ...form, deadline: e.target.value })}
                  className="rounded-xl"
                  data-testid="todo-deadline-input"
                />
              </div>
            </div>
            <div>
              <label className="label-eyebrow block mb-1.5">Lista</label>
              <Select value={form.list_type} onValueChange={(v) => setForm({ ...form, list_type: v })}>
                <SelectTrigger className="rounded-xl" data-testid="todo-list-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="shared">Gemensam (hela familjen)</SelectItem>
                  <SelectItem value="personal">Personlig (bara du)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {users.length > 1 && (
              <div>
                <label className="label-eyebrow block mb-1.5">Ansvarig</label>
                <Select
                  value={form.assignee_id || "none"}
                  onValueChange={(v) => setForm({ ...form, assignee_id: v === "none" ? null : v })}
                >
                  <SelectTrigger className="rounded-xl" data-testid="todo-assignee-select">
                    <SelectValue placeholder="Välj familjemedlem" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Ingen</SelectItem>
                    {users.map((u) => (
                      <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} data-testid="todo-cancel-button">
              Avbryt
            </Button>
            <Button
              onClick={save}
              className="bg-[color:var(--moss)] hover:bg-[#1f421f]"
              data-testid="todo-save-button"
            >
              Spara
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
